/**
 * 统一错误处理器
 * 将 ConnectError 和标准错误映射为可展示的错误结构。
 */

import { ConnectError, Code } from '@connectrpc/connect';
import { Errno } from '@puppycad/proto';
import { ApiErrnoError } from './ApiErrnoError';
import { isKnownErrno, resolveErrnoChineseMessage } from './errno-message';

export interface ResolvedError {
  /** gRPC 状态码或通用错误标识 */
  code: Code | string;

  /** 协议错误码；普通 Error 或未知错误可能没有该字段。 */
  errno?: Errno;

  /** 可直接展示给用户的错误消息 */
  message: string;

  /** 原始错误对象 */
  originalError: unknown;
}

/**
 * 将任意错误解析为标准化的 ResolvedError 结构
 *
 * @param error 待解析的未知错误对象。
 * @returns 标准化错误结构。
 */
export function resolveError(error: unknown): ResolvedError {
  // 1. 处理 ConnectError
  if (error instanceof ConnectError) {
    return resolveConnectError(error);
  }

  // 2. 处理流式事件或业务响应中携带的协议 Errno
  if (error instanceof ApiErrnoError) {
    return resolveApiErrnoError(error);
  }

  // 3. 处理普通 Error
  if (error instanceof Error) {
    return {
      code: 'UNKNOWN',
      message: error.message,
      originalError: error,
    };
  }

  // 4. 兜底
  return {
    code: 'UNKNOWN',
    message: '发生未知错误',
    originalError: error,
  };
}

/**
 * 将 gRPC 状态码映射为协议 Errno。
 *
 * 协议约定 0-16 与 google.rpc.Code 数值兼容，所以这里先按数值复用；
 * 如果未来 ConnectError 携带业务 ErrorDetail，应在此之前优先解析业务 errno。
 *
 * @param code gRPC 状态码。
 * @returns 对应协议 Errno。
 */
function resolveGrpcCodeErrno(code: Code): Errno {
  if (isKnownErrno(code)) {
    return code;
  }

  return Errno.ERRNO_UNKNOWN;
}

/**
 * 解析 ConnectError。
 *
 * @param error Connect RPC 错误。
 * @returns 标准化错误结构。
 */
function resolveConnectError(error: ConnectError): ResolvedError {
  const errno = resolveGrpcCodeErrno(error.code);

  return {
    code: error.code,
    errno,
    message: resolveErrnoChineseMessage(errno),
    originalError: error,
  };
}

/**
 * 解析 API 协议错误。
 *
 * @param error 携带协议 Errno 的 API 错误。
 * @returns 标准化错误结构。
 */
function resolveApiErrnoError(error: ApiErrnoError): ResolvedError {
  return {
    code: 'ERRNO',
    errno: error.errno,
    message: resolveErrnoChineseMessage(error.errno),
    originalError: error,
  };
}

